import React from "react";
import classnames from "classnames";

import { HStack } from "@formant/ui/component/common/HStack";

import { formatTime } from "../../../utils/formatTime";
import { copyToClipboard } from "../../common/copyToClipboard";
import { DeviceName } from "../../common/DeviceName";
import { IconButton } from "../../common/IconButton";
import { Tags } from "../../common/Tags";
import { DeepPureStateComponent } from "../../DeepPureStateComponent";
import { Lazy } from "../../Lazy";
import { IMetadataDataPoint } from "../utils/IMetadataDataPoint";
import { IJsonLoaderResult, JsonLoader } from "./JsonLoader";
import { maxPayloadSize } from "./JsonLoaderConstants";
import styles from "./JsonModuleListItem.scss";

const JsonPreview = Lazy(
    async () => (await import("./JsonPreview")).JsonPreview,
);

export interface IJsonModuleListItemProps {
    point: IMetadataDataPoint<"json">;
    old: boolean;
    live: boolean;
    hideDevice?: boolean;
    utc: boolean;
    onClick: (point: IMetadataDataPoint<"json">) => void;
    onClose: () => void;
    selected: boolean;
    jsonLoader: JsonLoader;
}
interface IJsonModuleListItemState {
    value?: IJsonLoaderResult;
}
export class JsonModuleListItem extends DeepPureStateComponent<
    IJsonModuleListItemProps,
    IJsonModuleListItemState
> {
    state: IJsonModuleListItemState = { value: undefined };

    public render() {
        this.props.jsonLoader
            .load(this.props.point.value)
            .then(value => this.setState({ value }))
            .catch(_ => {
                throw _;
            });

        const { onClickRow, onClose } = this;
        const { point, old, live, hideDevice, selected } = this.props;
        const { deviceId, tags, time } = point;

        const payloadTooBig = (this.state.value?.length ?? 0) > maxPayloadSize;

        const content = !selected ? (
            (this.state.value?.preview ?? "{}")
        ) : !payloadTooBig ? (
            <JsonPreview value={this.state.value?.json ?? {}} />
        ) : (
            `Warning: JSON payload too large for preview. Limit is ${maxPayloadSize} characters.`
        );

        return (
            <div
                className={classnames(
                    styles["json-module-list-item"],
                    !old && styles.current,
                    !old && live && styles.live,
                    selected && styles.selected,
                )}
                onClick={onClickRow}
            >
                <HStack hAlign="space-between">
                    {!hideDevice && (
                        <div className={styles.device}>
                            <DeviceName id={deviceId} size="18" />
                        </div>
                    )}
                    <div className={styles.time}>
                        {formatTime(time, { milliseconds: false })}
                    </div>
                </HStack>

                <div className={styles.json}>
                    {selected && (
                        <HStack
                            hAlign="end"
                            className={styles.actions}
                            vAlign="center"
                        >
                            <IconButton
                                className={styles.copy}
                                icon="copy"
                                onClicked={this.onCopy}
                                data-tooltip="Copy JSON to clipboard"
                            />
                            <IconButton
                                icon="close"
                                onClicked={onClose}
                                className={styles.close}
                            />
                        </HStack>
                    )}

                    {content}
                </div>
                {!hideDevice && <Tags tags={tags} className={styles.tags} />}
            </div>
        );
    }

    private onClickRow = () => {
        const { point, onClick } = this.props;
        onClick(point);
    };

    private onClose = (_: void) => {
        const { onClose } = this.props;
        onClose();
    };

    private onCopy = (_: void) => {
        const { value } = this.state;
        const jsonString = value ? JSON.stringify(value.json) : "{}";
        copyToClipboard(jsonString);
    };
}
